import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject, Subscription } from "rxjs/Rx";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { debounceTime } from "rxjs/operator/debounceTime";
import { ResidentService } from "../shared/services/user/resident.service";
import { UnitService } from "../shared/unit/unit.service";
import { User } from "../shared/user/user";
import { Constants } from "../helper/constants";
import { FileUploadService } from "../shared/services/fileuploadservice";
import { LoaderService } from "../shared/services/loader.service";
import { PapaParseService } from "ngx-papaparse";
import { CommunityService } from "../shared/services/community/community.service";
import { PropertyService } from "../shared/property/property.service";
import { IMyDpOptions } from "mydatepicker";
import { AuthService } from "../shared/services/auth.service";
import * as EmailValidator from "email-validator";
import { BackendService } from "../shared/services/backend.service";

@Component({
  selector: "app-resident-management",
  templateUrl: "./resident-management.component.html",
  styleUrls: ["./resident-management.component.css"]
})
export class ResidentManagementComponent implements OnInit, OnDestroy {
  currentDate = new Date();
  property: any;

  myDatePickerOptions: IMyDpOptions = {
    dateFormat: "yyyy-mm-dd",
    editableDateField: false,
    disableSince: {
      year: this.currentDate.getFullYear(),
      month: this.currentDate.getMonth() + 1,
      day: this.currentDate.getDate() + 1
    }
  };

  private _alert = new Subject<string>();
  activeRouteIndex: number;
  propertyId: string;
  communityId: string;
  unitId: string;
  residents = [];
  modalReference: NgbModalRef;
  page = 1;
  community$: any;
  community: any;
  unit: any;
  property$: any;
  unit$: any;
  user: any;
  userMirrored: any;
  confirmationMessage = "";
  confirmationTitle = "";
  successMessage = "";
  errorMessage = "";
  genders = Constants.genders;
  query = "";
  file;
  fileName;
  fileSize;
  fileUploadErrorMessage;
  canUpload = false;
  sampleFileUrl = "https://bit.ly/2J54AHF";
  routeSubscription: Subscription;
  alertSubscription: Subscription;
  residentSubscription: Subscription;
  tempDateOfBirth;
  phoneNumberErrorMsg: string;
  validEmail: boolean;
  operatorId: string;
  actionType: string;

  constructor(
    private authService: AuthService,
    private communityService: CommunityService,
    private loaderService: LoaderService,
    private modalService: NgbModal,
    private residentService: ResidentService,
    private route: ActivatedRoute,
    private unitService: UnitService,
    private uploadService: FileUploadService,
    private csvParseService: PapaParseService,
    private propertyService: PropertyService
  ) {}

  ngOnInit(): void {
    this.loaderService.startLoader();
    this.operatorId = BackendService.generateUserId(this.authService.getAuthState().email);

    this.routeSubscription = this.route.params.subscribe(params => {
      this.activeRouteIndex = params.index;
      this.propertyId = params.propertyId;
      this.communityId = params.communityId;
      this.unitId = params.unitId;

      this.community$ = this.communityService.getCommunityById(params.communityId);

      this.community$.subscribe(data => {
        this.community = data;
      });

      this.unit$ = this.unitService.getUnitById(this.propertyId, this.unitId);
      this.unit$.subscribe(unit => {
        this.unit = unit;
      });

      this.property$ = this.propertyService.getPropertyById(this.communityId, this.propertyId);
      this.property$.subscribe(property => {
        this.property = property;
      });

      this.residentService.getResidents(this.unitId, this.communityId).subscribe(data => {
        this.residents = data;
        this.loaderService.stopLoader();
      });
    });
  }

  ngOnDestroy(): void {
    this.closeModal();
    this.routeSubscription.unsubscribe();
  }

  openBulkUploadModal(modal): void {
    this.openModal(modal, "md");
  }

  viewUser(addModal, user): void {
    this.user = user;
    if (this.user.email) {
      this.isEmail(this.user.email);
    }

    if (this.user.dateOfBirth.date) {
      this.tempDateOfBirth = this.user.dateOfBirth.date;
    } else {
      this.tempDateOfBirth = this.user.dateOfBirth;
      this.user.dateOfBirth = { date: this.user.dateOfBirth };
    }
    this.openModal(addModal, "md");
  }

  openImageUploader(): void {
    const element = document.getElementById("imageUploader") as HTMLElement;
    element.click();
  }

  openFileUploader(): void {
    const element = document.getElementById("fileUploader") as HTMLElement;
    element.click();
  }

  clearFileUpload(): void {
    this.fileName = "";
    this.fileSize = "";
    this.canUpload = false;
  }

  getFileUpload(event): void {
    const result = this.uploadService.verifyFileIsCSV(event);
    if (result.file) {
      this.file = result.file;
      this.fileName = result.file.name;
      this.fileSize = `${result.file.size}B`;
      this.canUpload = true;
    } else {
      this.fileUploadErrorMessage = result.message;
    }
  }

  uploadImage(event): void {
    this.loaderService.startLoader();
    const file = event.target.files[0];
    this.uploadService.upload("avatars", file).subscribe(avatarUrl => {
      this.userMirrored.avatar = avatarUrl;
      this.loaderService.stopLoader();
    });
  }

  upload(): void {
    this.loaderService.startLoader();
    this.csvParseService.parse(this.file, {
      complete: results => {
        const filtered = {};
        for (const item of results.data) {
          filtered[item[4]] = item;
        }

        this.residentService.import(Object.values(filtered), this.community, this.operatorId);
        this.clearFileUpload();
        this.loaderService.stopLoader();
        this.showAlert(true, "Residents uploaded successfully.");
      },
      error: () => {
        this.loaderService.stopLoader();
        this.showAlert(false, "Residents could not be uploaded. Please try again");
      }
    });
    this.closeModal();
  }

  createOrEditUser(modal, user: any): void {
    if (user) {
      this.closeModal();
    }
    this.userMirrored = { ...new User(), ...user };
    this.openModal(modal, "md");
  }

  async saveUser(): Promise<void> {
    this.loaderService.startLoader();
    this.userMirrored.operatorId = this.operatorId;
    this.userMirrored.dateOfBirth.date !== this.tempDateOfBirth
      ? (this.userMirrored.dateOfBirth = this.userMirrored.dateOfBirth.formatted)
      : (this.userMirrored.dateOfBirth = this.tempDateOfBirth);
    const result = await this.authService.authenticateNewUser(this.userMirrored.email);
    if (!result.state) {
      this.showAlert(result.state, result.message);
    } else {
      let response;
      this.userMirrored.address = await this.composeResidentAddress();

      const userId = result.message;

      if (userId && !this.userMirrored.id) {
        this.userMirrored.id = userId;
        response = await this.residentService.store(this.community, this.unitId, this.userMirrored);
      } else if (userId && this.userMirrored.id) {
        response = await this.residentService.update(this.community, this.userMirrored);
      }
      this.showAlert(response.state, response.message);
    }
    this.loaderService.stopLoader();
    this.closeModal();
  }

  confirmDelete(user: any, modal): void {
    const message = `Are you sure you want to delete ${user.firstName} ${user.lastName}?`;
    this.confirm("Delete Resident!", message, modal);
  }

  deleteUser(): void {
    this.closeModal();
    this.showAlert(true, "User deleted successfully.");
  }

  activateResident(): void {
    this.residentService.activate(this.communityId, this.user.id);
    this.closeModal();
    this.actionType = "";
    this.user = {};
    this.showAlert(true, "Account activated successfully.");
  }

  deactivateResident(): void {
    this.residentService.deactivate(this.communityId, this.user.id);
    this.closeModal();
    this.actionType = "";
    this.user = {};
    this.showAlert(true, "Account deactivated successfully.");
  }

  confirmAction(type, user: any, modal): void {
    this.user = user;
    this.actionType = type;
    const message = `Are you sure you want to ${type} ${user.firstName} ${user.lastName}"s account?`;
    this.confirm("Attention!", message, modal);
  }

  confirm(title, message, modal): void {
    this.confirmationTitle = title;
    this.confirmationMessage = message;
    this.openModal(modal, "sm");
  }

  openModal(modal, modalSize): void {
    this.modalReference = this.modalService.open(modal, { size: modalSize });
  }

  closeModal(): void {
    if (this.modalReference) {
      this.modalReference.close();
    }
  }

  showAlert(msgType, msg): void {
    this.alertSubscription = this._alert.subscribe(
      message => (msgType ? (this.successMessage = message) : (this.errorMessage = message))
    );
    debounceTime
      .call(this._alert, 3000)
      .subscribe(() => (msgType ? (this.successMessage = undefined) : (this.errorMessage = undefined)));
    this._alert.next(msg);
    this.alertSubscription.unsubscribe();
  }

  isNumber(event: any): boolean {
    const charCode = event.keyCode;
    this.phoneNumberErrorMsg =
      this.userMirrored.phoneNumber.length === 11 ? "Phone number cannot be more than 11." : "";

    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }

    return true;
  }

  isLetter(event: any): boolean {
    const letters = /^[A-Za-z]+$/;
    const charKey = event.key;
    if (!charKey.match(letters)) {
      return false;
    }

    return true;
  }

  isEmail(email: string): void {
    this.validEmail = EmailValidator.validate(email);
  }

  async composeResidentAddress(): Promise<string> {
    let address = "";
    if (this.property && this.community) {
      const commAddress =
        await this.residentService.composeCommunityAddress(this.community.location.address);

      address +=
        this.unit.name + ", " + this.property.address + ", " + commAddress;
    }

    return address;
  }

  downloadUnits(): void {
    this.unitService.getUnits(this.propertyId).subscribe(units => {
      units = units.map((unit, index) => {
        return { "S/N": ++index, Name: unit["name"], "Unit Id": unit["id"] };
      });
      BackendService.downloadEntities(units, "units");
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/resident-management/resident-management.component.ts